import { faker } from '@faker-js/faker';

import type {
  CostReportAllocationGroupSummaryItem,
  CostReportAllocationGroupWorkload,
  CostReportRaw,
  NamespaceCostReportSummaryItem,
  NamespaceReportResponse,
  OrganizationClustersReportResponseClusterSummary,
  OrganizationClustersReportResponseSummary,
  ReportCapabilitiesResponse,
  WorkloadCostReportItem,
} from '@cast/types';

/**
 * TPU mock data generator options
 */
type TpuDataOptions = {
  /** Whether to include TPU data (default: true) */
  hasTPU?: boolean;
  /** Fixed TPU count (if not provided, random) */
  tpuCount?: number;
  /** Fixed TPU cost (if not provided, random) */
  tpuCost?: number;
};

/** Format number as string (matches API format) */
const toString = (value: number) => value.toFixed(6);

/** Random TPU count */
const randomTpuCount = () => faker.datatype.float({ min: 0, max: 16 });

/** Random TPU cost */
const randomTpuCost = () => faker.datatype.float({ min: 10, max: 200 });

/**
 * Create mock reporting capabilities response with TPU flags
 */
export const createMockTpuCapabilities = (
  options: {
    tpuDataAvailable?: boolean;
    tpuDataReceived?: boolean;
    gpuDataAvailable?: boolean;
    gpuMetricsAvailable?: boolean;
    networkDataAvailable?: boolean;
  } = {}
): ReportCapabilitiesResponse => ({
  tpuDataAvailable: options.tpuDataAvailable ?? true,
  tpuDataReceived: options.tpuDataReceived ?? true,
  gpuDataAvailable: options.gpuDataAvailable ?? true,
  gpuMetricsAvailable: options.gpuMetricsAvailable ?? false,
  networkDataAvailable: options.networkDataAvailable ?? false,
  reportingDataAvailable: true,
  reportingDataReceived: true,
  networkDataReceived: false,
  gpuDataReceived: options.gpuDataAvailable ?? true,
  gpuMetricsReceived: false,
});

/**
 * Create mock cluster cost report item with TPU data
 */
export const createMockClusterCostItemWithTpu = (
  options: TpuDataOptions = {}
): CostReportRaw => {
  const hasTPU = options.hasTPU ?? true;
  const tpuCount = hasTPU ? options.tpuCount ?? randomTpuCount() : 0;
  const tpuCost = hasTPU ? options.tpuCost ?? randomTpuCost() : 0;

  // Simple 60/30/10 split
  const tpuCountSpot = tpuCount * 0.6;
  const tpuCountOnDemand = tpuCount * 0.3;
  const tpuCountFallback = tpuCount * 0.1;
  const tpuCostSpot = tpuCost * 0.6;
  const tpuCostOnDemand = tpuCost * 0.3;
  const tpuCostSpotFallback = tpuCost * 0.1;

  return {
    timestamp: faker.date.recent().toISOString(),
    cpuCostOnDemand: toString(faker.datatype.float({ min: 10, max: 50 })),
    cpuCostSpot: toString(faker.datatype.float({ min: 5, max: 25 })),
    cpuCostSpotFallback: toString(faker.datatype.float({ min: 0, max: 5 })),
    gpuCostOnDemand: toString(faker.datatype.float({ min: 0, max: 30 })),
    gpuCostSpot: toString(faker.datatype.float({ min: 0, max: 20 })),
    gpuCostSpotFallback: toString(faker.datatype.float({ min: 0, max: 5 })),
    tpuCostOnDemand: toString(tpuCostOnDemand),
    tpuCostSpot: toString(tpuCostSpot),
    tpuCostSpotFallback: toString(tpuCostSpotFallback),
    storageCost: toString(faker.datatype.float({ min: 1, max: 10 })),
    cpuCountOnDemand: toString(faker.datatype.float({ min: 10, max: 100 })),
    cpuCountSpot: toString(faker.datatype.float({ min: 10, max: 100 })),
    cpuCountSpotFallback: toString(faker.datatype.float({ min: 0, max: 10 })),
    gpuCountOnDemand: toString(faker.datatype.float({ min: 0, max: 8 })),
    gpuCountSpot: toString(faker.datatype.float({ min: 0, max: 8 })),
    gpuCountSpotFallback: toString(faker.datatype.float({ min: 0, max: 2 })),
    tpuCountOnDemand: toString(tpuCountOnDemand),
    tpuCountSpot: toString(tpuCountSpot),
    tpuCountSpotFallback: toString(tpuCountFallback),
    storageGib: toString(faker.datatype.float({ min: 50, max: 200 })),
  };
};

/**
 * Create mock organization cluster cost report item with TPU summary
 */
export const createMockOrgClusterCostItemWithTpu = (
  options: TpuDataOptions & { clusterId?: string; clusterName?: string } = {}
): OrganizationClustersReportResponseClusterSummary => {
  const hasTPU = options.hasTPU ?? true;
  const tpuCount = hasTPU ? options.tpuCount ?? randomTpuCount() : 0;
  const tpuCost = hasTPU ? options.tpuCost ?? randomTpuCost() : 0;
  const clusterId = options.clusterId ?? faker.datatype.uuid();
  const clusterName =
    options.clusterName ?? `${faker.random.word()}-${faker.random.word()}`;

  return {
    clusterId,
    clusterName,
    clusterDeletedAt: null,
    summary: {
      cpuCost: toString(faker.datatype.float({ min: 50, max: 200 })),
      gpuCost: toString(faker.datatype.float({ min: 0, max: 100 })),
      tpuCost: toString(tpuCost),
      ramCost: toString(faker.datatype.float({ min: 20, max: 80 })),
      storageCost: toString(faker.datatype.float({ min: 10, max: 50 })),
      cpuProvisioned: toString(faker.datatype.float({ min: 100, max: 500 })),
      gpuProvisioned: toString(faker.datatype.float({ min: 0, max: 16 })),
      tpuProvisioned: toString(tpuCount),
      ramProvisioned: toString(faker.datatype.float({ min: 500, max: 2000 })),
      storageProvisioned: toString(
        faker.datatype.float({ min: 500, max: 5000 })
      ),
      cpuRequested: toString(faker.datatype.float({ min: 50, max: 400 })),
      gpuRequested: toString(faker.datatype.float({ min: 0, max: 12 })),
      tpuRequested: toString(tpuCount * 0.8),
      ramRequested: toString(faker.datatype.float({ min: 300, max: 1500 })),
      storageRequested: toString(faker.datatype.float({ min: 300, max: 3000 })),
      cpuAllocatable: toString(faker.datatype.float({ min: 100, max: 500 })),
      gpuAllocatable: toString(faker.datatype.float({ min: 0, max: 16 })),
      tpuAllocatable: toString(tpuCount),
      ramAllocatable: toString(faker.datatype.float({ min: 500, max: 2000 })),
      storageClaimed: toString(faker.datatype.float({ min: 500, max: 5000 })),
    },
  };
};

/**
 * Create mock organization clusters summary with TPU totals
 */
export const createMockOrgClustersSummaryWithTpu = (
  options: TpuDataOptions = {}
): OrganizationClustersReportResponseSummary => {
  const hasTPU = options.hasTPU ?? true;
  const tpuCount = hasTPU ? options.tpuCount ?? randomTpuCount() * 5 : 0;
  const tpuCost = hasTPU ? options.tpuCost ?? randomTpuCost() * 5 : 0;

  return {
    cpuCost: toString(faker.datatype.float({ min: 500, max: 2000 })),
    gpuCost: toString(faker.datatype.float({ min: 0, max: 500 })),
    tpuCost: toString(tpuCost),
    ramCost: toString(faker.datatype.float({ min: 200, max: 800 })),
    storageCost: toString(faker.datatype.float({ min: 100, max: 500 })),
    cpuProvisioned: toString(faker.datatype.float({ min: 1000, max: 5000 })),
    gpuProvisioned: toString(faker.datatype.float({ min: 0, max: 80 })),
    tpuProvisioned: toString(tpuCount),
    ramProvisioned: toString(faker.datatype.float({ min: 5000, max: 20000 })),
    storageProvisioned: toString(
      faker.datatype.float({ min: 5000, max: 50000 })
    ),
    cpuRequested: toString(faker.datatype.float({ min: 500, max: 4000 })),
    gpuRequested: toString(faker.datatype.float({ min: 0, max: 60 })),
    tpuRequested: toString(tpuCount * 0.8),
    ramRequested: toString(faker.datatype.float({ min: 3000, max: 15000 })),
    storageRequested: toString(faker.datatype.float({ min: 3000, max: 30000 })),
    cpuAllocatable: toString(faker.datatype.float({ min: 1000, max: 5000 })),
    gpuAllocatable: toString(faker.datatype.float({ min: 0, max: 80 })),
    tpuAllocatable: toString(tpuCount),
    ramAllocatable: toString(faker.datatype.float({ min: 5000, max: 20000 })),
    storageClaimed: toString(faker.datatype.float({ min: 5000, max: 50000 })),
  };
};

/**
 * Create mock namespace cost summary item with TPU data (for list endpoints)
 */
export const createMockNamespaceCostSummaryItemWithTpu = (
  options: TpuDataOptions & { namespace?: string } = {}
): NamespaceCostReportSummaryItem => {
  const hasTPU = options.hasTPU ?? true;
  const tpuCount = hasTPU ? options.tpuCount ?? randomTpuCount() : 0;
  const tpuCost = hasTPU ? options.tpuCost ?? randomTpuCost() : 0;

  return {
    namespace: options.namespace ?? faker.random.word(),
    cost: {
      workloadCount: toString(faker.datatype.number({ min: 1, max: 20 })),
      cpuCount: toString(faker.datatype.float({ min: 10, max: 100 })),
      ramGib: toString(faker.datatype.float({ min: 50, max: 200 })),
      gpuCount: toString(faker.datatype.float({ min: 0, max: 4 })),
      tpuCount: toString(tpuCount),
      cpuCost: toString(faker.datatype.float({ min: 5, max: 20 })),
      ramCost: toString(faker.datatype.float({ min: 3, max: 15 })),
      gpuCost: toString(faker.datatype.float({ min: 0, max: 10 })),
      tpuCost: toString(tpuCost),
      costOnDemand: toString(faker.datatype.float({ min: 20, max: 80 })),
      costSpot: toString(faker.datatype.float({ min: 10, max: 40 })),
      costSpotFallback: toString(faker.datatype.float({ min: 0, max: 10 })),
      totalCost: toString(faker.datatype.float({ min: 50, max: 200 })),
      storageGib: toString(faker.datatype.float({ min: 10, max: 100 })),
      storageCost: toString(faker.datatype.float({ min: 1, max: 5 })),
    },
  };
};

/**
 * Create mock single namespace report response with TPU data
 */
export const createMockNamespaceReportWithTpu = (
  options: TpuDataOptions & { namespace?: string; clusterId?: string } = {}
): NamespaceReportResponse => {
  const hasTPU = options.hasTPU ?? true;
  const tpuCost = hasTPU ? options.tpuCost ?? randomTpuCost() : 0;

  return {
    clusterId: options.clusterId ?? faker.datatype.uuid(),
    namespace: options.namespace ?? faker.random.word(),
    costSummary: {
      totalCost: toString(faker.datatype.float({ min: 50, max: 200 })),
      avgCost: toString(faker.datatype.float({ min: 10, max: 50 })),
      avgCpuCost: toString(faker.datatype.float({ min: 5, max: 20 })),
      avgRamCost: toString(faker.datatype.float({ min: 3, max: 15 })),
      avgGpuCost: toString(faker.datatype.float({ min: 0, max: 10 })),
      avgTpuCost: toString(tpuCost),
      avgStorageCost: toString(faker.datatype.float({ min: 1, max: 5 })),
      totalWorkloadsCount: toString(faker.datatype.number({ min: 1, max: 20 })),
    },
    dailyCostItems: [],
  };
};

/**
 * Create mock workload cost item with TPU data
 */
export const createMockWorkloadCostItemWithTpu = (
  options: TpuDataOptions & {
    workloadName?: string;
    namespace?: string;
    workloadType?: string;
  } = {}
): WorkloadCostReportItem => {
  const hasTPU = options.hasTPU ?? true;
  const tpuCount = hasTPU ? options.tpuCount ?? randomTpuCount() : 0;
  const tpuCost = hasTPU ? options.tpuCost ?? randomTpuCost() : 0;

  // Simple 60/30/10 split
  const tpuCountSpot = tpuCount * 0.6;
  const tpuCountOnDemand = tpuCount * 0.3;
  const tpuCountFallback = tpuCount * 0.1;
  const tpuCostSpot = tpuCost * 0.6;
  const tpuCostOnDemand = tpuCost * 0.3;
  const tpuCostFallback = tpuCost * 0.1;

  const workloadTypes = [
    'Deployment',
    'StatefulSet',
    'DaemonSet',
    'Job',
    'CronJob',
  ];

  const costMetrics = hasTPU
    ? [
        {
          timestamp: faker.date.recent().toISOString(),
          tpuCountOnDemand: toString(tpuCountOnDemand),
          tpuCountSpot: toString(tpuCountSpot),
          tpuCountSpotFallback: toString(tpuCountFallback),
          tpuCostOnDemand: toString(tpuCostOnDemand),
          tpuCostSpot: toString(tpuCostSpot),
          tpuCostSpotFallback: toString(tpuCostFallback),
        },
      ]
    : [];

  return {
    workloadName: options.workloadName ?? faker.random.word(),
    namespace: options.namespace ?? faker.random.word(),
    workloadType:
      options.workloadType ?? faker.helpers.arrayElement(workloadTypes),
    costMetrics,
    labels: [],
  };
};

/**
 * Create mock allocation group summary with TPU data
 */
export const createMockAllocationGroupSummaryWithTpu = (
  options: TpuDataOptions & {
    groupId?: string;
    groupName?: string;
  } = {}
): CostReportAllocationGroupSummaryItem => {
  const hasTPU = options.hasTPU ?? true;
  const tpuCount = hasTPU ? options.tpuCount ?? randomTpuCount() : 0;
  const tpuCost = hasTPU ? options.tpuCost ?? randomTpuCost() : 0;

  return {
    groupId: options.groupId ?? faker.datatype.uuid(),
    groupName: options.groupName ?? faker.company.bsBuzz(),
    summary: {
      totalCostOnDemand: toString(faker.datatype.float({ min: 20, max: 80 })),
      totalCostSpot: toString(faker.datatype.float({ min: 10, max: 40 })),
      totalCostSpotFallback: toString(
        faker.datatype.float({ min: 0, max: 10 })
      ),
      cpuCount: toString(faker.datatype.float({ min: 10, max: 100 })),
      ramGib: toString(faker.datatype.float({ min: 50, max: 200 })),
      gpuCount: toString(faker.datatype.float({ min: 0, max: 4 })),
      tpuCount: toString(tpuCount),
      cpuCost: toString(faker.datatype.float({ min: 10, max: 50 })),
      ramCost: toString(faker.datatype.float({ min: 5, max: 20 })),
      gpuCost: toString(faker.datatype.float({ min: 0, max: 20 })),
      tpuCost: toString(tpuCost),
      workloadCount: toString(faker.datatype.number({ min: 1, max: 20 })),
    },
  };
};

/**
 * Create mock allocation group workload with TPU data
 */
export const createMockAllocationGroupWorkloadWithTpu = (
  options: TpuDataOptions & {
    workloadName?: string;
    namespace?: string;
    workloadType?: string;
    clusterId?: string;
    clusterName?: string;
  } = {}
): CostReportAllocationGroupWorkload => {
  const hasTPU = options.hasTPU ?? true;
  const tpuCount = hasTPU ? options.tpuCount ?? randomTpuCount() * 0.3 : 0;
  const tpuCost = hasTPU ? options.tpuCost ?? randomTpuCost() * 0.3 : 0;

  const workloadTypes = [
    'Deployment',
    'StatefulSet',
    'DaemonSet',
    'Job',
    'CronJob',
  ];

  return {
    clusterId: options.clusterId ?? faker.datatype.uuid(),
    workloadName: options.workloadName ?? faker.random.word(),
    workloadType:
      options.workloadType ?? faker.helpers.arrayElement(workloadTypes),
    namespace: options.namespace ?? faker.random.word(),
    totalCostOnDemand: toString(faker.datatype.float({ min: 5, max: 20 })),
    totalCostSpot: toString(faker.datatype.float({ min: 3, max: 15 })),
    totalCostSpotFallback: toString(faker.datatype.float({ min: 0, max: 3 })),
    cpuCount: toString(faker.datatype.float({ min: 1, max: 10 })),
    ramGib: toString(faker.datatype.float({ min: 5, max: 50 })),
    gpuCount: toString(faker.datatype.float({ min: 0, max: 2 })),
    tpuCount: toString(tpuCount),
    cpuCost: toString(faker.datatype.float({ min: 1, max: 10 })),
    ramCost: toString(faker.datatype.float({ min: 0.5, max: 5 })),
    gpuCost: toString(faker.datatype.float({ min: 0, max: 5 })),
    tpuCost: toString(tpuCost),
    totalPodCount: toString(faker.datatype.number({ min: 1, max: 10 })),
    podCount: toString(faker.datatype.number({ min: 1, max: 5 })),
  };
};

/**
 * Create mock cluster summary with TPU data
 */
export const createMockClusterSummaryWithTpu = (
  options: TpuDataOptions = {}
): OrganizationClustersReportResponseSummary => {
  const hasTPU = options.hasTPU ?? true;
  const tpuCount = hasTPU ? options.tpuCount ?? randomTpuCount() : 0;
  const tpuCost = hasTPU ? options.tpuCost ?? randomTpuCost() : 0;

  return {
    cpuCost: toString(faker.datatype.float({ min: 50, max: 200 })),
    gpuCost: toString(faker.datatype.float({ min: 0, max: 100 })),
    tpuCost: toString(tpuCost),
    ramCost: toString(faker.datatype.float({ min: 20, max: 80 })),
    storageCost: toString(faker.datatype.float({ min: 10, max: 50 })),
    cpuProvisioned: toString(faker.datatype.float({ min: 100, max: 500 })),
    gpuProvisioned: toString(faker.datatype.float({ min: 0, max: 16 })),
    tpuProvisioned: toString(tpuCount),
    ramProvisioned: toString(faker.datatype.float({ min: 500, max: 2000 })),
    storageProvisioned: toString(faker.datatype.float({ min: 500, max: 5000 })),
    cpuRequested: toString(faker.datatype.float({ min: 50, max: 400 })),
    gpuRequested: toString(faker.datatype.float({ min: 0, max: 12 })),
    tpuRequested: toString(tpuCount * 0.8),
    ramRequested: toString(faker.datatype.float({ min: 300, max: 1500 })),
    storageRequested: toString(faker.datatype.float({ min: 300, max: 3000 })),
    cpuAllocatable: toString(faker.datatype.float({ min: 100, max: 500 })),
    gpuAllocatable: toString(faker.datatype.float({ min: 0, max: 16 })),
    tpuAllocatable: toString(tpuCount),
    ramAllocatable: toString(faker.datatype.float({ min: 500, max: 2000 })),
    storageClaimed: toString(faker.datatype.float({ min: 500, max: 5000 })),
  };
};

/**
 * Generate an array of mock items with dynamic count
 */
export const generateMockArray = <T>(
  factory: (index: number) => T,
  minCount = 10,
  maxCount = 30
): T[] => {
  const count = faker.datatype.number({ min: minCount, max: maxCount });
  return Array.from({ length: count }, (_, index) => factory(index));
};
